// src/pages/RedliningPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, CircularProgress, Grid, Paper, Divider } from '@mui/material';
import { documentService } from '../services/documentService'; // Use documentService instead
import DocumentCard from '../components/DocumentCard'; // Assuming you have this component

const RedliningPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setLoading(true);
        // Use documentService like the Dashboard does
        const data = await documentService.getUserDocuments(
          1, // page
          100, // limit
          'uploaded', // activeTab
          '', // searchQuery
          'all' // statusFilter
        );
        setDocuments(data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, []);

  const handleRedlineDocument = (documentId) => {
    navigate(`/redlining/${documentId}`);
  };

  const handleAnalyzeDocument = async (documentId) => {
    try {
      navigate(`/redlining/${documentId}?analyze=true`);
    } catch (err) {
      console.error('Error starting document analysis:', err);
      setError('Failed to start document analysis. Please try again.');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Document Redlining
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Select a document to review and add redline comments. Use AI assistance to identify concerning clauses and suggest improvements.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper sx={{ p: 3, bgcolor: '#fff9f9' }}>
            <Typography color="error">{error}</Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => window.location.reload()} 
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Paper>
        ) : documents.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6">No documents found</Typography>
            <Typography variant="body2" color="textSecondary">
              Upload a document first to start redlining.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/dashboard')} 
              sx={{ mt: 2 }}
            >
              Go to Dashboard
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {documents.map((document) => (
              <Grid item xs={12} sm={6} md={4} key={document.id}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <Typography variant="h6" gutterBottom noWrap>
                    {document.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {new Date(document.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {document.description || 'No description'}
                  </Typography>
                  <Box flexGrow={1} />
                  <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
                    <Button 
                      variant="outlined" 
                      color="secondary"
                      onClick={() => handleAnalyzeDocument(document.id)}
                      size="small"
                    >
                      AI Analyze
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => handleRedlineDocument(document.id)}
                      size="small"
                    >
                      Redline
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default RedliningPage;